import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { RECORDING_DEBOUNCE_TIME } from '@/components/constants';
import { patientConsentModalOpenAtom } from '@/components/modal/patient-consent-modal.atoms';
import { useRecordingSessionId } from '@/hooks/useCurrentIds';
import {
  cancelRecordingControlAttempt,
  type RecordingControlOptions,
  type RecordingControlTracking,
} from '@/lib/telemetry/recordingControlTracking';
import {
  finishRecordingAttempt,
  type RecordingAttemptTracking,
  type RecordingStartOptions,
} from '@/lib/telemetry/recordingStartTracking';
import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { store, type Dispatch } from '@/store';
import { isTauri } from '@/tauri/utils';
import { logConsentGateDecision } from '@/utils/logger/consent-logging';
import { logger } from '@/utils/logger/logger';
import { RecordingStatus, SessionPages, TranscribeMode } from '@heidi/schema/ml-scribe';
import {
  addStartNowStoppedSessionId,
  removeStartNowStoppedSessionId,
  startNowStoppedSessionIdsAtom,
  useQuerySessionById,
} from '@heidi/session/data';
import { useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { useMutateStartTauriRecording } from '../mutation/useMutateStartTauriRecording';
import { useMutateStopRecording } from '../mutation/useMutateStopRecording';
import { useMutateUpdateSessionDuration } from '../mutation/useMutateUpdateSessionDuration';
import { useMutateUpdateSessionRecordingStatus } from '../mutation/useMutateUpdateSessionRecordingStatus';
import { TabType, useSessionWindows } from '../query/useQuerySessionWindows';
import { useQueryUser } from '../query/useQueryUser';
import { useTutorial } from '../useTutorial';

export const useRecordingV1 = () => {
  const sessionId = useRecordingSessionId();
  const setReadyToRequestMicPerms = useSetAtom(readyToRequestMicPermissionsAtom);

  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        created_time_local: data.created_time_local,
        created_at: data.created_at,
        consult_note: data.consult_note,
        duration: data.duration,
        is_consent: data.is_consent,
        transcribe_mode: data.transcribe_mode,
        generate_output_without_recording: data.generate_output_without_recording,
        patient_profile: data.patient_profile,
        recording_status: data.recording_status,
        session_gist: data.session_gist,
        streamingStatus: data.streamingStatus,
      };
    },
  });
  const { data: userData } = useQueryUser();
  const { mutate: startTauriRecording } = useMutateStartTauriRecording();

  const { tutorialStepCurrentPage, onNextPage, panelOpen } = useTutorial();
  const shouldHighlightStartRecording =
    tutorialStepCurrentPage?.enum === SessionPages.RUN_CONSULT && panelOpen;
  const shouldHighlightStopRecording =
    tutorialStepCurrentPage?.enum === SessionPages.STOP_SESSION && panelOpen;

  const setPatientConsentModalOpen = useSetAtom(patientConsentModalOpenAtom);
  const setStoppedSessionIds = useSetAtom(startNowStoppedSessionIdsAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const { mutate: updateRecordingStatus } = useMutateUpdateSessionRecordingStatus();
  const { mutate: stopRecordingAudio } = useMutateStopRecording({
    sessionId: sessionId,
  });
  const { ensureTabFocus } = useSessionWindows({ sessionId });
  const dispatch = useDispatch<Dispatch>();

  const debouncedOnRecordingPress = useDebouncedCallback(
    (tracking?: RecordingAttemptTracking) => {
      // TODO: combine these two actions into one
      if (isTauri()) {
        const isResumeOfPausedSession =
          session?.recording_status === RecordingStatus.PAUSED;
        startTauriRecording({
          sessionId: sessionId,
          /* Captured from this render's subscription, which still holds the
             pre-press status: useRecordingButton's play path optimistically
             rewrites the cached recording_status to RECORDING (in
             useMutateUpdateSessionRecordingStatus.onMutate) before the start
             mutation runs, so the mutation cannot recover PAUSED from the
             cache itself. */
          isResumeOfPausedSession,
          tracking,
        });
        return;
      }
      if (session?.transcribe_mode) {
        try {
          void dispatch.scribeRecordModel.startRecordingButtonPressed({
            sessionId: sessionId,
            transcribeMode: session?.transcribe_mode,
            onStop: () => {
              updateRecordingStatus({
                recordingStatus: RecordingStatus.IDLE,
                sessionId,
              });
            },
          });
        } catch (error) {
          logger.error({
            event_type: 'START_ERROR_V1',
            details: {
              errorName: error instanceof Error ? error.constructor.name : typeof error,
              errorMessage: error instanceof Error ? error.message : String(error),
            },
          });
          updateRecordingStatus({
            recordingStatus: RecordingStatus.IDLE,
            sessionId,
          });
        }
      }
    },
    RECORDING_DEBOUNCE_TIME,
    {
      leading: true,
      trailing: false,
    }
  );

  const onStartButtonPressed = useCallback(
    (props?: RecordingStartOptions) => {
      setReadyToRequestMicPerms(true);
      const { shouldBypassConsentCheck = false, tracking } = props || {};

      if (!sessionId) {
        if (tracking) {
          finishRecordingAttempt(tracking, {
            outcome: 'failed',
            error: createSessionFlowPreflightError(
              'StartRecordingFailed',
              'No session to record'
            ),
          });
        }
        return;
      }
      if (shouldHighlightStartRecording) {
        onNextPage();
      }
      const shouldBlockRecordingWithoutConsent =
        Boolean(userData?.session_settings?.is_consent_pop_up) &&
        session?.transcribe_mode !== TranscribeMode.DICTATE &&
        !session?.duration &&
        !session?.is_consent &&
        !session?.patient_profile?.remember_consent &&
        !shouldBypassConsentCheck;

      // Bypass re-entry (modal confirm, paused-session resume) is not a
      // decision, so it must not emit a pass row.
      if (!shouldBypassConsentCheck) {
        logConsentGateDecision({
          gate: 'recording_v1',
          sessionId,
          settingsLoaded: userData?.session_settings !== undefined,
          isConsentPopUpSetting: userData?.session_settings?.is_consent_pop_up,
          sessionIsConsent: session?.is_consent,
          sessionDuration: session?.duration,
          rememberConsent: session?.patient_profile?.remember_consent,
          transcribeMode: session?.transcribe_mode,
          willShowConsentPopup: shouldBlockRecordingWithoutConsent,
        });
      }

      if (shouldBlockRecordingWithoutConsent) {
        setPatientConsentModalOpen(true);
        return;
      }

      // The debounce drops this call, so its attempt ends here. The call still
      // goes through so it extends the debounce window as before.
      if (tracking && debouncedOnRecordingPress.isPending()) {
        finishRecordingAttempt(tracking, {
          outcome: 'failed',
          error: createSessionFlowPreflightError(
            'StartRecordingCancelled',
            'Recording press dropped by the start debounce'
          ),
          metadata: { preflight_gate: 'recording_press_debounced' },
        });
        debouncedOnRecordingPress();
        return;
      }
      debouncedOnRecordingPress(tracking);
    },
    [
      setReadyToRequestMicPerms,
      sessionId,
      shouldHighlightStartRecording,
      userData?.session_settings,
      session?.transcribe_mode,
      session?.duration,
      session?.is_consent,
      session?.patient_profile?.remember_consent,
      debouncedOnRecordingPress,
      onNextPage,
      setPatientConsentModalOpen,
    ]
  );

  const { mutate: updateSessionDuration } = useMutateUpdateSessionDuration();

  const onStopButtonPressed = useCallback(
    (isPaused: boolean, tracking?: RecordingControlTracking) => {
      if (!sessionId) {
        // No session: drop the attempt.
        cancelRecordingControlAttempt(tracking);
        return;
      }
      if (shouldHighlightStopRecording) {
        onNextPage();
      }

      if (!isPaused) {
        setStoppedSessionIds((current) =>
          addStartNowStoppedSessionId(current, sessionId)
        );
      }
      stopRecordingAudio(
        { isPaused, tracking },
        {
          onSuccess: () => {
            if (!isPaused) {
              setActiveSessionId(null);
            }
          },
          onError: () => {
            if (!isPaused) {
              setStoppedSessionIds((current) =>
                removeStartNowStoppedSessionId(current, sessionId)
              );
            }
          },
        }
      );
      // Only ending a recording should switch to Note while it generates.
      // Pausing must preserve the current active tab in either session layout.
      if (!isPaused) {
        ensureTabFocus(TabType.NOTE);
      }
      updateSessionDuration(
        {
          sessionId,
          duration: Number(store.getState().timerModel.seconds),
        },
        {
          onError: () => {
            if (!isPaused) {
              setStoppedSessionIds((current) =>
                removeStartNowStoppedSessionId(current, sessionId)
              );
            }
          },
        }
      );
    },
    [
      sessionId,
      stopRecordingAudio,
      updateSessionDuration,
      shouldHighlightStopRecording,
      onNextPage,
      ensureTabFocus,
      setStoppedSessionIds,
      setActiveSessionId,
    ]
  );

  // The 1s repeat window lives in useRecordingCommandPolicy, which the command boundary applies.
  // Stable across renders through a ref; the options object must never reach
  // the `isPaused` argument.
  const onStopButtonPressedRef = useRef(onStopButtonPressed);
  onStopButtonPressedRef.current = onStopButtonPressed;
  const stopRecording = useCallback(
    (options?: RecordingControlOptions) =>
      onStopButtonPressedRef.current(false, options?.tracking),
    []
  );
  const pauseRecording = useCallback(
    (options?: RecordingControlOptions) =>
      onStopButtonPressedRef.current(true, options?.tracking),
    []
  );

  return {
    stopRecording,
    startRecording: onStartButtonPressed,
    pauseRecording,
  };
};
